import { createApi } from '@reduxjs/toolkit/query/react';

import { cabinetInternetPackageCategories } from '@/constants/cabinet';

import cabinetBaseQuery from '../../api/cabinet';
import { BASE_URL } from '../../constants';

export const cabinetApi = createApi({
  reducerPath: 'cabinetApi',
  baseQuery: cabinetBaseQuery(),
  tagTypes: ['Cabinet'],
  endpoints: (builder) => ({
    subscriberCheck: builder.query({
      query: ({ msisdn, lang, accessToken, recaptcha, oneTime = false }) => ({
        params: {
          oneTime,
        },
        url: `/prod/account-management/primary-account/check/${msisdn}`,
        method: 'GET',
        headers: {
          'Accept-Language': lang,
          Authorization: accessToken,
          'g-recaptcha-response': recaptcha,
          deviceType: 'WEB',
        },
      }),
    }),
    secondarySubscriberCheck: builder.query({
      query: ({ msisdn, lang, accessToken }) => ({
        params: {
          msisdn,
        },
        url: '/prod/account-management/subscriber/check',
        method: 'GET',
        headers: { 'Accept-Language': lang, Authorization: accessToken },
      }),
    }),
    otpRequest: builder.mutation({
      query: ({ phoneNumber, lang }) => ({
        url: '/prod/account-management/otp',
        method: 'POST',
        headers: { 'Accept-Language': lang },
        data: { phoneNumber },
      }),
      invalidatesTags: ['Cabinet'],
    }),
    signUp: builder.mutation({
      query: ({ data, lang }) => ({
        url: '/prod/account-management/primary-account/sign-up',
        method: 'POST',
        data,
        headers: { 'Accept-Language': lang },
      }),
    }),
    getAccounts: builder.query({
      query: ({ lang, accessToken, oneTime }) => ({
        url: '/prod/bakcell-account-dashboard/accounts',
        method: 'GET',
        headers: { 'Accept-Language': lang, Authorization: accessToken },
        params: {
          oneTime,
        },
      }),
      providesTags: ['Cabinet'],
    }),
    refreshToken: builder.mutation({
      query: ({ refreshToken }) => ({
        url: '/prod/account-management/primary-account/refresh-tokens',
        method: 'POST',
        headers: { 'refresh-Token': refreshToken },
      }),
    }),
    getChartDetail: builder.query({
      query: ({ accountId, lang }) => ({
        url: `/prod/bakcell-account-dashboard/account-dashboard/${accountId}/chart-detail`,
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
    }),
    getBalance: builder.query({
      query: ({ accountId, lang }) => ({
        url: `/prod/bakcell-account-dashboard/account-dashboard/${accountId}/balance`,
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
    }),
    freeUnit: builder.query({
      query: ({ accountId, lang }) => ({
        url: `/prod/bakcell-account-dashboard/account-dashboard/${accountId}/free-unit`,
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
    }),
    getTariffs: builder.query({
      query: ({ accountId, lang }) => ({
        url: '/prod/bakcell-tariff/tariffs',
        params: {
          accountId,
          sortBy: 'POPULARITY',
        },
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
    }),
    changeTariff: builder.mutation({
      query: ({
        lang,
        tariffId,
        accountId,
        renewing = false,
        accessToken,
      }) => ({
        url: `/prod/bakcell-tariff/tariffs/${tariffId}`,
        method: 'PUT',
        data: {
          accountId,
          renewing,
        },
        headers: { 'Accept-Language': lang, Authorization: accessToken },
      }),
    }),
    getSubscriptionInfo: builder.query({
      query: ({ accountId, lang }) => ({
        url: `/prod/bakcell-account-dashboard/account-dashboard/${accountId}/subscription`,
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
    }),
    secondaryAccount: builder.mutation({
      query: ({ data, accessToken, lang }) => ({
        url: '/prod/account-management/secondary-accounts',
        method: 'POST',
        headers: { 'Accept-Language': lang, Authorization: accessToken },
        data,
      }),
      invalidatesTags: ['Cabinet'],
    }),
    deleteAccount: builder.mutation({
      query: ({ numberId, accessToken, lang }) => ({
        url: `/prod/account-management/secondary-accounts/${numberId}`,
        method: 'DELETE',
        baseURL: BASE_URL,
        headers: { 'Accept-Language': lang, Authorization: accessToken },
      }),
      invalidatesTags: ['Cabinet'],
    }),
    signOut: builder.mutation({
      query: ({ lang }) => ({
        url: '/prod/account-management/primary-account/sign-out',
        method: 'POST',
        headers: { 'Accept-Language': lang },
      }),
    }),
    getPackages: builder.query({
      query: ({ accountId, lang }) => ({
        url: '/prod/bakcell-packages/packages',
        params: {
          requestType: 'SERVICE',
          accountId,
        },
        method: 'GET',
        headers: { 'Accept-Language': lang },
      }),
      transformResponse: (packages) => {
        return {
          filteredInternetCategories: [
            cabinetInternetPackageCategories[0],
            ...cabinetInternetPackageCategories.filter((category) => {
              return packages?.some(
                (item) => item.filterType === category.value,
              );
            }),
          ],
          packages,
        };
      },
    }),
    changePackage: builder.mutation({
      query: ({ lang, accountId, packageId, accessToken }) => {
        return {
          url: '/prod/bakcell-packages/packages/activate',
          method: 'PATCH',
          params: {
            accountId,
            packageId,
          },
          headers: { 'Accept-Language': lang, Authorization: accessToken },
        };
      },
    }),
    topUpWithoutCard: builder.mutation({
      query: ({ lang, accessToken, amount, receiver = null }) => {
        return {
          url: '/prod/bakcell-payment/pay/without-card',
          method: 'POST',
          headers: { 'Accept-Language': lang, Authorization: accessToken },
          data: {
            amount,
          },
          params: {
            receiver,
            web: true,
          },
        };
      },
    }),
  }),
});

export const hooks = {
  useSubscriberCheck: cabinetApi.useLazySubscriberCheckQuery,
  useOtpRequest: cabinetApi.useOtpRequestMutation,
  useSignUp: cabinetApi.useSignUpMutation,
  useGetAccounts: cabinetApi.useLazyGetAccountsQuery,
  useGetAccountsQuery: cabinetApi.useGetAccountsQuery,
  useRefreshToken: cabinetApi.useRefreshTokenMutation,
  useGetChartDetail: cabinetApi.useGetChartDetailQuery,
  useGetBalance: cabinetApi.useGetBalanceQuery,
  useFreeUnit: cabinetApi.useFreeUnitQuery,
  useSecondaryAccount: cabinetApi.useSecondaryAccountMutation,
  useGetTariffs: cabinetApi.useGetTariffsQuery,
  useGetSubscriptionInfo: cabinetApi.useGetSubscriptionInfoQuery,
  useChangeTariff: cabinetApi.useChangeTariffMutation,
  useGetPackages: cabinetApi.useGetPackagesQuery,
  useChangePackage: cabinetApi.useChangePackageMutation,
  useDeleteAccount: cabinetApi.useDeleteAccountMutation,
  useSignOut: cabinetApi.useSignOutMutation,
  useTopUpWithoutCard: cabinetApi.useTopUpWithoutCardMutation,
  useSecondarySubscriberCheck: cabinetApi.useLazySecondarySubscriberCheckQuery,
};

export default cabinetApi;
